import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  FormHelperText,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import CustomerDropDownWithLock from './components/CustomerDropDownWithLock';
import { Lock } from '@material-ui/icons';
import {
  ArticleTypeToText,
  ArticleTypes,
  getHeadings,
  lengthOfArticle,
  writingStyles,
} from '../layouts/QuickBriefGenerator';
import { fetchAIBasedContent } from '../../utility/cr-common';
import { QuattrLogoSpinner } from './components/QuattrLogoSpinner';
import { AxiosError } from 'axios';
import { capitalize } from 'lodash';
import ReCAPTCHA from 'react-google-recaptcha';
import { CONTINUE_USING_TEXT, calculateRows } from './components/ToolsStyleWrapper';
import {
  COMPONENT_NAMES,
  SCREEN_NAMES,
  CONTENT_AI_TOOL_NAME,
  CONTENT_AI_TOOL_CATEGORY,
  trackAnalyticsEvent,
} from '../../../src/constants/analyticsMeta';
import {
  getToolsUsageCreditsNeeded,
  handleGenerateOutput,
  PAID_TOOLS_PROMPT_FOR,
  resetCaptcha,
} from '../../utility/common';
import Cookies from 'js-cookie';
import { SkipCaptcha } from './components/SkipCaptcha';
import { useSession } from '../../utility/services/customHooks';
import { SUBSCRIPTION_STATUS } from '../../constants/common';
import CreditsInfo from './components/CreditsInfo';

const useStyles = makeStyles((theme) => ({
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const WriteContentBrief = ({
  classes,
  setShowSignUpPage,
  user,
  hasValidSubscription,
  resetLockedGrace,
  enableLockedOption,
  handleResetUsage,
  isQwaDashboard = false,
  isCreditsShown = false,
  creditsRemaining = 0,
  usagePerDay = 0,
  setShowBuyOnePass,
  setIsRedirectToWebflow,
  setShowMinimumCreditsNeededMsg = (a) => {},
  setShowVerifyEmailPopup,
  freeToolsUsage = 0,
  showFreeToolsUsage = false,
  isCorpEmail = false,
}) => {
  const ownClasses = useStyles();
  const qttr_tools_auth = Cookies.get('qttr_tools_auth');
  const qttr_tools_daypass_auth = Cookies.get('qttr_tools_daypass_auth');
  const [pageTopic, setPageTopic] = useState('');
  const [articleType, setArticleType] = useState('Blog');
  const [writingStyle, setWritingStyle] = useState('Formal');
  const [wordCount, setWordCount] = useState('1,000 Words');
  const [targetAudience, setTargetAudience] = useState('');
  const [outcome, setOutCome] = useState('');
  const [loading, setLoading] = useState(false);
  const [contentBrief, setContentBrief] = useState('');
  const [isMounted, setIsMounted] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [suggestions, setSuggestions] = useState({
    title: false,
    H1: false,
    meta: false,
    H2: false,
    H3: false,
    H4: false,
    images: false,
    schema: false,
  });
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const [creditsNeeded, setCreditsNeeded] = useState(1);
  const inputSelectionRef = useRef<HTMLInputElement>(null);
  const session = useSession();

  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
    setCaptchaError('');
  };

  useEffect(() => {
    trackAnalyticsEvent({
      isAnonymousUser: !user,
      payload: {
        label: 'Visited Free Tool',
        component_name: COMPONENT_NAMES.contentai_ai_tools,
        screen_name: SCREEN_NAMES.contentai_ai_tools,
        tool_name: CONTENT_AI_TOOL_NAME.content_brief_generator,
        tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_writing_tools,
        isQwaDashboard: isQwaDashboard,
      },
    });
  }, []);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleCheckboxChange = (name) => {
    setSuggestions((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const generateContentBrief = async () => {
    try {
      if (!pageTopic) {
        setError('pageTopic');
        return;
      }
      if (!(recaptchaValue || qttr_tools_auth || user)) {
        setCaptchaError('recaptchaValue');
        return;
      }
      setCaptchaError('');
      setError('');
      setLoading(true);
      const isInputValid = pageTopic && articleType && writingStyle && wordCount;
      if (!isInputValid) {
        setError('Both keyword and description are required');
        return;
      }
      const selectedOptions = Object.keys(suggestions).filter((i) => suggestions[i]);
      const headingsSelected = getHeadings(selectedOptions.filter((i) => ['H2', 'H3', 'H4'].includes(i)));
      const otherTags = selectedOptions.filter((i) => !['H2', 'H3', 'H4', 'Schema'].includes(i));
      const withWebPageSchemaM = selectedOptions.includes('Schema') ? 'and web page schema markup' : '';

      const res = await fetchAIBasedContent({
        promptFor: 'PUBLIC_UTILITY.CONTENT_BRIEF',
        promptConfig: {
          articleTypeText: ArticleTypeToText[articleType] || '',
          articleType,
          pageTopic,
          writingStyle,
          articleLength: wordCount,
          outcome,
          initialHeadingSuggestions: headingsSelected,
          suggestSchema: selectedOptions.includes('Schema') ? 'true' : '',
          additionalInstructions: additionalInstructions,
          otherTags: otherTags ? otherTags.join(',') : '',
          withWebPageSchemaM,
          selectedOptions: selectedOptions,
          targetAudience: targetAudience,
        },
        tokenLimit: 3500,
        isPublicApi: true,
        reCAPTCHAToken: recaptchaValue,
      });
      if (res) {
        trackAnalyticsEvent({
          eventFor: 'button',
          buttonText: '',
          counterName: `${CONTENT_AI_TOOL_CATEGORY.ai_content_writing_tools} Outputs`,
          isAddCount: true,
          user,
          isAnonymousUser: !user,
          payload: {
            label: 'Generate Output',
            component_name: COMPONENT_NAMES.contentai_ai_tools,
            screen_name: SCREEN_NAMES.contentai_ai_tools,
            tool_name: CONTENT_AI_TOOL_NAME.content_brief_generator,
            tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_writing_tools,
            number_suggestions: Object.values(suggestions).filter(Boolean).length,
            special_instructions_yn: additionalInstructions ? 'Yes' : 'No',
            isQwaDashboard: isQwaDashboard,
            creditsConsumed: creditsNeeded ? creditsNeeded : 1,
          },
        });
        if (handleResetUsage) {
          handleResetUsage();
        }
      }
      setContentBrief(res);
      if (resetLockedGrace) {
        resetLockedGrace();
        setTargetAudience('');
        setSuggestions({ ...suggestions, images: false, schema: false });
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response) {
        if (axiosError.response.status === 403) {
          setShowSignUpPage(CONTINUE_USING_TEXT, {
            tool_name: CONTENT_AI_TOOL_NAME.content_brief_generator,
            tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_writing_tools,
          });
          trackAnalyticsEvent({
            eventFor: 'button',
            isAnonymousUser: !user,
            payload: {
              label: 'Reached your maximum usage limit',
              component_name: COMPONENT_NAMES.contentai_ai_tools,
              screen_name: SCREEN_NAMES.contentai_ai_tools,
              tool_name: CONTENT_AI_TOOL_NAME.content_brief_generator,
              tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_writing_tools,
              isQwaDashboard: isQwaDashboard,
            },
          });
        }
      }
      console.error('generateContentBrief::error', error);
    } finally {
      setRecaptchaValue(null);
      resetCaptcha();
      setLoading(false);
    }
  };

  const handleCopyText = () => {
    const inputElement = inputSelectionRef?.current as any;
    if (inputElement) {
      const copiedText = inputElement?.value?.substring(
        inputElement?.selectionStart || 0,
        inputElement?.selectionEnd || 0
      );
      if (copiedText) {
        navigator.clipboard.writeText(copiedText);
        setCopied(true);
        trackAnalyticsEvent({
          eventFor: 'button',
          buttonText: '',
          counterName: 'Outputs Accepted',
          isAddCount: true,
          user,
          isAnonymousUser: !user,
          payload: {
            label: 'Copy Generated Output',
            component_name: COMPONENT_NAMES.contentai_ai_tools,
            screen_name: SCREEN_NAMES.contentai_ai_tools,
            tool_name: CONTENT_AI_TOOL_NAME.content_brief_generator,
            tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_writing_tools,
            number_suggestions: Object.values(suggestions).filter(Boolean).length,
            special_instructions_yn: additionalInstructions ? 'Yes' : 'No',
            isQwaDashboard: isQwaDashboard,
            isManualCopy: true,
          },
        });
      } else {
        navigator.clipboard.writeText(contentBrief);
        setCopied(true);
        trackAnalyticsEvent({
          eventFor: 'button',
          buttonText: '',
          counterName: 'Outputs Accepted',
          isAddCount: true,
          user,
          isAnonymousUser: !user,
          payload: {
            label: 'Copy Generated Output',
            component_name: COMPONENT_NAMES.contentai_ai_tools,
            screen_name: SCREEN_NAMES.contentai_ai_tools,
            tool_name: CONTENT_AI_TOOL_NAME.content_brief_generator,
            tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_writing_tools,
            number_suggestions: Object.values(suggestions).filter(Boolean).length,
            special_instructions_yn: additionalInstructions ? 'Yes' : 'No',
            isQwaDashboard: isQwaDashboard,
          },
        });
      }
    }

    setTimeout(() => {
      setCopied(false);
    }, 2000); // Change the text back to 'Copy Text' after 2 seconds
  };
  const UpdatedSelectedOptions = Object.keys(suggestions).filter((i) => suggestions[i]);

  useEffect(() => {
    const promptFor = PAID_TOOLS_PROMPT_FOR.CONTENT_BRIEF_GENERATOR;
    const promptConfig = {
      selectedOptions: UpdatedSelectedOptions,
      targetAudience: targetAudience,
    };
    setCreditsNeeded(getToolsUsageCreditsNeeded(promptFor, promptConfig));
  }, [user, UpdatedSelectedOptions, targetAudience]);

  return (
    <>
      {contentBrief ? (
        <div className={classes.container}>
          <TextField
            inputRef={inputSelectionRef}
            fullWidth
            multiline
            rows={calculateRows(contentBrief)}
            margin="normal"
            placeholder="Generated Title Tag"
            value={contentBrief}
            className={`${classes.textAreaInput} no-border background-gray`}
            maxRows={200}
            InputProps={{
              style: { borderRadius: '4px !important', padding: '16px' },
            }}
            minRows={20}
            variant="outlined"
            onCopy={handleCopyText}
          />
          <div className={classes.buttonGroup} style={{ marginTop: '16px' }}>
            <Button
              className={`${classes.toolsYellowCTA} ${classes.toolsOutlinedCTA} ${classes.button}`}
              disabled={loading}
              style={{ float: 'right' }}
              onClick={() => {
                setContentBrief('');
                trackAnalyticsEvent({
                  eventName: 'Clicked on Go back',
                  payload: {
                    label: 'Clicked on Go back',
                  },
                });
              }}
            >
              Go back
            </Button>
            <Button
              className={`${classes.toolsYellowCTA} ${classes.button}`}
              disabled={loading}
              style={{ float: 'right' }}
              onClick={handleCopyText}
            >
              {copied ? 'Copied!' : 'Copy Text'}
            </Button>
          </div>
        </div>
      ) : isMounted ? (
        <>
          <div className={classes.container}>
            {loading && <QuattrLogoSpinner classes={classes} />}
            <TextField
              fullWidth
              label="Enter Page Topic"
              value={pageTopic}
              variant="outlined"
              className={`${classes.textAreaInput} no-border`}
              onChange={(e) => setPageTopic(e.target.value)}
              error={!!error}
              helperText={error.includes('pageTopic') ? '* Required' : error.includes('Please sign up') ? error : ''}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <CustomerDropDownWithLock
                  label="Type of Article"
                  value={articleType}
                  classes={classes}
                  options={ArticleTypes} // Populate with actual options
                  onChange={(e) => {
                    setArticleType(e.target.value as string);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomerDropDownWithLock
                  label="Writing Style"
                  value={writingStyle}
                  classes={classes}
                  options={writingStyles} // Populate with actual options
                  onChange={(e) => {
                    setWritingStyle(e.target.value as string);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomerDropDownWithLock
                  label="Tentative Word Count"
                  value={wordCount}
                  classes={classes}
                  options={lengthOfArticle} // Populate with actual options
                  onChange={(e) => {
                    setWordCount(e.target.value as string);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  placeholder="Target Audience"
                  variant="outlined"
                  inputProps={{
                    readOnly: !hasValidSubscription && !enableLockedOption ? true : false,
                  }}
                  className={`${classes.textAreaInput} Mui-disabled  no-border m-0`}
                  value={targetAudience}
                  onClick={() => {
                    if (setShowSignUpPage && !hasValidSubscription && !enableLockedOption) {
                      setShowSignUpPage("'Add target Audience'", {
                        tool_name: CONTENT_AI_TOOL_NAME.content_brief_generator,
                        tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_writing_tools,
                      });
                      trackAnalyticsEvent({
                        eventFor: 'button',
                        isAnonymousUser: !user,
                        payload: {
                          label: 'Clicked Locked Option',
                          component_name: COMPONENT_NAMES.contentai_ai_tools,
                          screen_name: SCREEN_NAMES.contentai_ai_tools,
                          tool_name: CONTENT_AI_TOOL_NAME.content_brief_generator,
                          tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_writing_tools,
                          option_name: 'Add target Audience',
                          isQwaDashboard: isQwaDashboard,
                        },
                      });
                    }
                  }}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  InputProps={{
                    endAdornment:
                      !hasValidSubscription && !enableLockedOption ? (
                        <InputAdornment position="end">
                          <Lock className={classes.lockIcon} />
                        </InputAdornment>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M9 12.9525L13.635 15.75L12.405 10.4775L16.5 6.93L11.1075 6.4725L9 1.5L6.8925 6.4725L1.5 6.93L5.595 10.4775L4.365 15.75L9 12.9525Z"
                            fill="#FFC72C"
                          />
                        </svg>
                      ),
                  }}
                />
              </Grid>
            </Grid>
            <TextField
              className={`${classes.textAreaInput} no-border mb-0`}
              fullWidth
              multiline
              rows={4}
              maxRows={4}
              variant="outlined"
              label="Describe the outcome you are hoping to achieve from this page"
              value={outcome}
              onChange={(e) => setOutCome(e.target.value)}
            />
            <div>
              <h3 className={classes.sectionSubHeading}>Add Suggestions For</h3>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    {Object.keys(suggestions).map((key, index) => (
                      <Grid item xs={4} sm={3} key={index}>
                        <FormControlLabel
                          control={
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <Checkbox
                                checked={suggestions[key]}
                                onChange={() => {
                                  if (
                                    hasValidSubscription ||
                                    enableLockedOption ||
                                    (key !== 'images' && key !== 'schema')
                                  ) {
                                    handleCheckboxChange(key);
                                  } else if (!hasValidSubscription && !enableLockedOption) {
                                    setShowSignUpPage(`"get ${key} Suggestions"`, {
                                      tool_name: CONTENT_AI_TOOL_NAME.content_brief_generator,
                                      tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_writing_tools,
                                    });
                                    trackAnalyticsEvent({
                                      eventFor: 'button',
                                      isAnonymousUser: !user,
                                      payload: {
                                        label: 'Clicked Locked Option',
                                        component_name: COMPONENT_NAMES.contentai_ai_tools,
                                        screen_name: SCREEN_NAMES.contentai_ai_tools,
                                        tool_name: CONTENT_AI_TOOL_NAME.content_brief_generator,
                                        tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_writing_tools,
                                        option_name: `${key} Suggestions`,
                                        isQwaDashboard: isQwaDashboard,
                                      },
                                    });
                                  }
                                }}
                                icon={
                                  (key === 'images' || key === 'schema') &&
                                  !hasValidSubscription &&
                                  !enableLockedOption ? (
                                    <Lock style={{ marginRight: 8 }} />
                                  ) : undefined
                                }
                              />
                            </div>
                          }
                          label={
                            (key === 'images' || key === 'schema') && (hasValidSubscription || enableLockedOption) ? (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                {capitalize(key)}
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="18"
                                  height="18"
                                  viewBox="0 0 18 18"
                                  fill="none"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M9 12.9525L13.635 15.75L12.405 10.4775L16.5 6.93L11.1075 6.4725L9 1.5L6.8925 6.4725L1.5 6.93L5.595 10.4775L4.365 15.75L9 12.9525Z"
                                    fill="#FFC72C"
                                  />
                                </svg>
                              </span>
                            ) : key ? (
                              capitalize(key)
                            ) : null
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </div>
            <TextField
              fullWidth
              multiline
              label="Additional instructions you would like to include"
              value={additionalInstructions}
              onChange={(e) => setAdditionalInstructions(e.target.value)}
              className={`${classes.textAreaInput} no-border`}
              rows={4}
              maxRows={4}
              variant="outlined"
            />
            {!qttr_tools_auth && !user && (
              <>
                <ReCAPTCHA
                  className={classes.recaptcha}
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                  onChange={handleRecaptchaChange}
                />
                {captchaError?.includes('recaptchaValue') && (
                  <FormHelperText className="red">Please complete the reCAPTCHA to verify you're human.</FormHelperText>
                )}
                <SkipCaptcha
                  classes={classes}
                  meta={{
                    component_name: COMPONENT_NAMES.contentai_ai_tools,
                    screen_name: SCREEN_NAMES.contentai_ai_tools,
                    tool_name: CONTENT_AI_TOOL_NAME.content_brief_generator,
                    tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_writing_tools,
                  }}
                  setShowVerifyEmailPopup={setShowVerifyEmailPopup}
                />
              </>
            )}

            <div className={`${isCreditsShown ? classes.creditsShown : classes.freeCreditsShown}`}>
              {isCreditsShown ? (
                <CreditsInfo
                  classes={classes}
                  creditsRemaining={creditsRemaining}
                  creditsNeeded={creditsNeeded}
                  usagePerDay={usagePerDay}
                />
              ) : !isQwaDashboard || showFreeToolsUsage ? (
                <CreditsInfo
                  classes={classes}
                  creditsRemaining={freeToolsUsage}
                  creditsNeeded={creditsNeeded}
                  usagePerDay={isCorpEmail ? 10 : 7}
                  showFreeToolsUsage={showFreeToolsUsage}
                />
              ) : null}
              <div className={`d-flex ${classes.buttonGroup}`} style={{ marginTop: '16px' }}>
                <Button
                  className={classes.toolsYellowCTA}
                  onClick={() =>
                    handleGenerateOutput(
                      creditsRemaining,
                      creditsNeeded,
                      isQwaDashboard,
                      session,
                      qttr_tools_daypass_auth,
                      setShowMinimumCreditsNeededMsg,
                      setShowBuyOnePass,
                      generateContentBrief,
                      trackAnalyticsEvent,
                      freeToolsUsage,
                      showFreeToolsUsage
                    )
                  }
                >
                  Generate Content Brief
                </Button>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
};

export default WriteContentBrief;
